import {
    OfferSummaryCard,
    createOfferSummaryCard,
    IOfferSummaryCardPromoPrice,
    IOfferSummaryCardAdditionalPrice,
} from '@vfde-brix/ws10/offer-summary-card';
import {
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
    stripHtml,
} from '@vfde-brix/ws10/core';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import {
    IHighlightBadgeProperties,
    HighlightBadgeColor,
} from '@vfde-brix/ws10/highlight-badge';
import { IButtonProperties } from '@vfde-brix/ws10/button';
import {
    createId,
    replacePlaceholders,
} from '@vfde-sails/utils';
import { getShippingFee } from '@vfde-sails/page-options';
import { IStickyPriceBarItem } from '@vfde-brix/ws10/sticky-price-bar';
import { initEditorialOverlays } from '@vfde-sails/overlay';
import { startAppListening } from '../../../app/listener';
import {
    RootState,
    useAppDispatch,
} from '../../../app/store';
import { mountStickyPriceBar } from '../../../components/StickyPriceBar';
import {
    getHighlightBadgeContent,
    getHeadline,
    getPriceCondition,
    shouldShowMonthlyPrice,
    shouldShowTariffInformation,
    getConnectionFee,
    getPrimaryButtonProps,
    shouldShowVvlLink,
    getSubline,
    getLegalText,
} from '../helpers/offerSummaryCardHelper';
import {
    selectAtomicDevice,
    selectTariff,
    selectDevice,
    selectHardwareOneTimePrice,
    selectMonthlyPriceToPay,
    selectMonthlyEndPriceToPay,
    selectMonthlyStrikePrice,
    selectHasStairway,
} from '../selectors';
import {
    selectCustomerConfig,
    selectSkipConnectMore,
} from '../slice';

/**
 * Mounts the OfferSummaryCard
 */
export const mountOfferSummaryCard = (
    containerId: string,
    stickyPriceBarContainerId: string,
): OfferSummaryCard | null => {
    const container = document.getElementById(containerId);

    /* istanbul ignore if */
    if (!container) {
        return null;
    }

    const offerSummaryCard = createOfferSummaryCard(container, NO_PATTERN_BUSINESS_LOGIC);
    const stickyPriceBar = mountStickyPriceBar(stickyPriceBarContainerId);

    // connect offerSummaryCard with stickyPriceBar
    stickyPriceBar && offerSummaryCard.connectStickyPriceBar(stickyPriceBar);

    listenForUpdates(offerSummaryCard);

    return offerSummaryCard;
};

const listenForUpdates = (
    offerSummaryCard: OfferSummaryCard,
) => {
    startAppListening({
        predicate: (_action, currentState, previousState) =>
            (
                selectAtomicDevice(currentState) !== selectAtomicDevice(previousState)
                || selectTariff(currentState) !== selectTariff(previousState)
                || selectCustomerConfig(currentState).insurance !== selectCustomerConfig(previousState).insurance
                || selectCustomerConfig(currentState).isHardwareOnly !== selectCustomerConfig(previousState).isHardwareOnly
                || selectCustomerConfig(currentState).isTauschbonus !== selectCustomerConfig(previousState).isTauschbonus
                || selectSkipConnectMore(currentState) !== selectSkipConnectMore(previousState)
            ),
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();

            updateOfferSummaryCard(state, offerSummaryCard);
        },
    });
};

/**
 * Detects and performs the relevant updates based on the old and new state
 */
export const updateOfferSummaryCard = (
    state: RootState,
    offerSummaryCard: OfferSummaryCard | null,
) => {
    const device = selectDevice(state);

    /* istanbul ignore if */
    if (!offerSummaryCard || !device) {
        return;
    }

    updateHighlightBadge(offerSummaryCard, state);
    updateHeadline(offerSummaryCard, state);
    updatePrices(offerSummaryCard, state);
    updateButtons(offerSummaryCard, state);
    updateTexts(offerSummaryCard, state);

    const dispatch = useAppDispatch();
    initEditorialOverlays(dispatch);
};

const updateHighlightBadge = (
    offerSummaryCard: OfferSummaryCard,
    state: RootState,
) => {
    const originalProperties = offerSummaryCard.getOriginalProperties();
    const { containerHighlightBadge } = originalProperties;

    /* istanbul ignore if */
    if (!containerHighlightBadge) {
        // if there was no highlightBadge in the original properties,
        // we can't update it as our update is based on the highlightBadge from CMS
        return;
    }

    const highlightBadgeContent = getHighlightBadgeContent(state);
    const { text, tooltip, icon, color } = highlightBadgeContent || {};
    let newHighlightBadgeProps: Partial<IHighlightBadgeProperties> | null = null;

    /* istanbul ignore else */
    if (text) {
        newHighlightBadgeProps = {
            txtContent: text,
            optSize: containerHighlightBadge?.optSize,
            optColor: color as HighlightBadgeColor || containerHighlightBadge?.optColor,
            stdIcon: icon || containerHighlightBadge?.stdIcon,
            containerTooltip: tooltip ? {
                ...containerHighlightBadge?.containerTooltip,
                stdTooltipId: createId(tooltip?.headline, 'ddp-offer-tooltip'),
                stdHeadline: tooltip?.headline,
                txtContent: `<p>${tooltip?.text}</p>`,
                business: NO_PATTERN_BUSINESS_LOGIC,
            } : undefined,
        };
    }

    offerSummaryCard.updateHighlightBadge(newHighlightBadgeProps);
};

const updateHeadline = (
    offerSummaryCard: OfferSummaryCard,
    state: RootState,
) => {
    const device = selectDevice(state);
    const customerConfig = selectCustomerConfig(state);

    offerSummaryCard.updateHeadline({
        stdContent: getHeadline(device!.modelName, customerConfig),
    });
};

/**
 * The order of prices within the OfferSummaryCard should be:
 * - OneTimePrice (PromoPrice)
 * - MonthlyPrice (PromoPrice)
 * - Stairway (AdditionalPrice)
 * - ConnectionFee (AdditionalPrice)
 * - ShippingFee (AdditionalPrice)
 * If there is no ConnectionFee, a hint should be added to the legalText (see `updateTexts`)
 * @param offerSummaryCard The offerSummaryCard instance
 * @param state The current state
 */
const updatePrices = (
    offerSummaryCard: OfferSummaryCard,
    state: RootState,
) => {
    const originalProperties = offerSummaryCard.getOriginalProperties();
    const tariff = selectTariff(state);
    const { isHardwareOnly, insurance } = selectCustomerConfig(state);
    const stickyPriceBarItems: IStickyPriceBarItem[] = [];

    // #region 1. OneTimePrice

    const hardwareOneTimePrice = selectHardwareOneTimePrice(state);
    const oneTimePriceLabel = originalProperties.containerPromoPrices![0].txtLabel;
    const oneTimePrice = Price.fromNumber(hardwareOneTimePrice!.gross);
    const optPriceConditionBefore = getPriceCondition(!!tariff, isHardwareOnly);

    const promoPrices: IOfferSummaryCardPromoPrice[] = [
        {
            txtLabel: oneTimePriceLabel,
            containerPromoPrice: {
                ...originalProperties.containerPromoPrices![0].containerPromoPrice,
                price: oneTimePrice,
                optPriceConditionBefore,
            },
        },
    ];

    stickyPriceBarItems.push({
        stdText: stripHtml(oneTimePriceLabel),
        price: oneTimePrice,
        optPriceConditionBefore,
    });

    // #endregion

    // #region 2. MonthlyPrice

    const monthlyPriceToPay = selectMonthlyPriceToPay(state);
    const monthlyEndPriceToPay = selectMonthlyEndPriceToPay(state);
    const strikePrice = selectMonthlyStrikePrice(state);
    const showMonthlyPrice = shouldShowMonthlyPrice(!!tariff, !!insurance, isHardwareOnly);

    if (showMonthlyPrice && monthlyPriceToPay) {
        // priceToPay could temporarily be null when the tariff API request is still loading
        const monthlyPriceLabel = originalProperties.containerPromoPrices![1].txtLabel;
        const monthlyPrice = Price.fromNumber(monthlyPriceToPay.gross);

        promoPrices.push({
            txtLabel: monthlyPriceLabel,
            containerPromoPrice: {
                ...originalProperties.containerPromoPrices![1].containerPromoPrice,
                strikePrice: strikePrice ? Price.fromNumber(strikePrice.gross) : undefined,
                price: monthlyPrice,
            },
        });

        stickyPriceBarItems.push({
            stdText: stripHtml(monthlyPriceLabel),
            price: monthlyPrice,
        });
    }

    // #endregion

    // #region 3. Stairway

    const additionalPrices: IOfferSummaryCardAdditionalPrice[] = [];
    const hasStairway = selectHasStairway(state);

    if (hasStairway) {
        const stairwaylabel = replacePlaceholders(
            window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.stairwayLabel,
            {
                month: monthlyPriceToPay!.recurrenceEnd + 1,
            },
        );
        const stairwayPrice = Price.fromNumber(monthlyEndPriceToPay!.gross);

        additionalPrices.push({
            txtLabel: stairwaylabel,
            price: stairwayPrice,
        });

        stickyPriceBarItems.push({
            stdText: stairwaylabel,
            price: stairwayPrice,
        });
    }

    // #endregion

    // #region 4. ConnectionFee

    const showTariffInformation = shouldShowTariffInformation(!!tariff, isHardwareOnly);

    if (showTariffInformation) {
        const connectionFee = getConnectionFee(tariff);

        if (connectionFee) {
            additionalPrices.push({
                txtLabel: window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.connectionFeeLabel,
                price: Price.fromNumber(connectionFee),
            });
        }
    }

    // #endregion

    // #region 5. ShippingFee

    const shippingFee = getShippingFee();

    /* istanbul ignore else */
    if (shippingFee) {
        additionalPrices.push(
            {
                txtLabel: shippingFee.label,
                price: Price.fromNumber(shippingFee.gross),
            },
        );
    }

    offerSummaryCard.updatePrices(
        promoPrices,
        additionalPrices,
    );

    offerSummaryCard.stickyPriceBar?.update({ stickyPriceBarItems });

    // #endregion
};

const updateButtons = (
    offerSummaryCard: OfferSummaryCard,
    state: RootState,
) => {
    const subscription = selectTariff(state);
    const { isHardwareOnly, salesChannel } = selectCustomerConfig(state);
    const originalProperties = offerSummaryCard.getOriginalProperties();
    const primaryButtonProperties = getPrimaryButtonProps(state);

    const buttonProperties: IButtonProperties[] = [
        {
            ...originalProperties.containerButtons![0],
            ...primaryButtonProperties,
        },
    ];

    const showTariffInformation = shouldShowTariffInformation(!!subscription, isHardwareOnly);

    if (showTariffInformation) {
        const { tariffOverview } = window[ADDITIONAL_PAGE_OPTIONS];

        buttonProperties.push(
            {
                ...originalProperties.containerButtons![1],
                linkHref: tariffOverview[salesChannel],
            },
        );
    }

    offerSummaryCard.updateButtons(buttonProperties);
};

/**
 * Updates the text properties
 * @param offerSummaryCard The offerSummaryCard instance
 * @param state The current state
 */
const updateTexts = (
    offerSummaryCard: OfferSummaryCard,
    state: RootState,
) => {
    const customerConfig = selectCustomerConfig(state);

    const showVvlLink = shouldShowVvlLink(customerConfig.salesChannel!);
    const subline = getSubline(state);
    const legalText = getLegalText(state);
    const originalProperties = offerSummaryCard.getOriginalProperties();
    const additionalText = showVvlLink ? originalProperties.txtAdditionalText : null;

    offerSummaryCard.updateSubline(subline);
    offerSummaryCard.updateLegalText(legalText);
    offerSummaryCard.updateAdditionalText(additionalText);
};
